import { Box, Group, Text, type MantineSpacing } from '@mantine/core';
import { ChevronRight, Lightbulb, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactElement } from 'react';

export interface BreadcrumbItem {
    label: string;
    to?: string;
}

interface DrillBreadcrumbProps {
    items: BreadcrumbItem[];
    /** Bottom margin; set to 0 when the breadcrumb is laid out inside a row that owns the spacing. */
    mb?: MantineSpacing;
    /** Leading icon; defaults to the Insights lightbulb for the analytics drill pages. */
    icon?: LucideIcon;
}

/** Drill-trail across the L0 → L1 → L2 levels. Items with `to` are navigable. */
export default function DrillBreadcrumb({ items, mb = 'md', icon: Icon = Lightbulb }: DrillBreadcrumbProps): ReactElement {
    return (
        <Group gap={6} mb={mb} wrap="nowrap" style={{ overflow: 'hidden' }}>
            <Icon size={13} color="var(--bm-color-text-dimmed)" />
            {items.map((item, index) => {
                const isLast = index === items.length - 1;
                return (
                    <Group gap={6} key={`${item.label}-${index}`} wrap="nowrap">
                        {item.to !== undefined && !isLast ?
                            <Text
                                component={Link}
                                to={item.to}
                                size="xs"
                                fw={600}
                                c="dimmed"
                                style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                            >
                                {item.label}
                            </Text>
                        :   <Text size="xs" fw={isLast ? 700 : 600} c={isLast ? undefined : 'dimmed'} style={{ whiteSpace: 'nowrap' }} truncate>
                                {item.label}
                            </Text>
                        }
                        {!isLast && (
                            <Box style={{ display: 'flex' }}>
                                <ChevronRight size={12} color="var(--bm-color-text-dimmed)" />
                            </Box>
                        )}
                    </Group>
                );
            })}
        </Group>
    );
}
